import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { ReportingDataService } from '../shared/services/reporting-data.service';

@Component({
  selector: 'app-reporting-utilization',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule
  ],
  templateUrl: './reporting-utilization.component.html',
  styleUrl: './reporting-utilization.component.scss'
})
export class ReportingUtilizationComponent {

  get kpiCards() { return this.dataService.utilizationKpis; }
  get workOrderRows() { return this.dataService.workOrderRows; }
  get trendConfig() { return this.dataService.utilizationTrendConfig; }
  readonly pageFilters = ['Since Jan 1, 2025', 'All NAMCs', 'All Shops'];

  activeTab: 'overview' | 'edcUsage' | 'reportConsumption' = 'overview';

  readonly kpiCardMeta = [
    { icon: 'pi-chart-line', iconClass: 'blue' },
    { icon: 'pi-briefcase', iconClass: 'green' },
    { icon: 'pi-calendar', iconClass: 'purple' },
    { icon: 'pi-wrench', iconClass: 'orange' },
    { icon: 'pi-chart-bar', iconClass: 'sky' },
    { icon: 'pi-clock', iconClass: 'teal' }
  ];

  readonly funnelRows = [
    { label: 'Users with EDC Access', value: 2450, bar: 100, conversion: '' },
    { label: 'Logged into EDC', value: 2100, bar: 86, conversion: '86%' },
    { label: 'Created a PCR / WO', value: 1650, bar: 79, conversion: '79%' },
    { label: 'Viewed a Report', value: 1080, bar: 65, conversion: '65%' },
    { label: 'Viewed Weekly', value: 820, bar: 76, conversion: '76%' },
    { label: 'Viewed Monthly', value: 640, bar: 78, conversion: '78%' }
  ];

  readonly scatterRows = [
    { namc: 'TMMMS', x: 8, y: 4, size: 8, color: '#e24e44' },
    { namc: 'TMMGT', x: 42, y: 7.5, size: 7, color: '#f2943c' },
    { namc: 'TMMTX', x: 66, y: 11.2, size: 8, color: '#f5a623' },
    { namc: 'TMMK', x: 74, y: 13.8, size: 8, color: '#4caf72' },
    { namc: 'TMMC', x: 88, y: 16.5, size: 8, color: '#7e67f1' },
    { namc: 'TABC', x: 96, y: 14.8, size: 9, color: '#3b82f6' }
  ];

  readonly scoreBreakdown = [
    { label: 'EDC Usage', weight: 40, value: 76 },
    { label: 'Report Usage', weight: 30, value: 72 },
    { label: 'Returning Users', weight: 15, value: 65 },
    { label: 'Data Completeness', weight: 15, value: 70 }
  ];

  readonly topNamcs = [
    { namc: 'TABC', score: 94, trend: 'up', change: 5, sparkline: '8,14 18,12 28,10 38,11 48,8 58,6 68,4 78,5 88,3' },
    { namc: 'TMMC', score: 88, trend: 'up', change: 4, sparkline: '8,16 18,14 28,15 38,12 48,10 58,9 68,7 78,5 88,4' },
    { namc: 'TMMI', score: 79, trend: 'up', change: 3, sparkline: '8,15 18,13 28,14 38,12 48,11 58,10 68,8 78,7 88,6' },
    { namc: 'TMMTX', score: 62, trend: 'down', change: 2, sparkline: '8,4 18,6 28,7 38,9 48,11 58,12 68,13 78,15 88,17' },
    { namc: 'TMMMS', score: 48, trend: 'down', change: 3, sparkline: '8,5 18,6 28,8 38,10 48,11 58,13 68,14 78,16 88,18' }
  ];

  readonly lowestNamcs = [
    { namc: 'TMMMS', score: 48, trend: 'down', change: 3, sparkline: '8,6 18,8 28,10 38,11 48,12 58,13 68,15 78,16 88,18' },
    { namc: 'TMMGT', score: 51, trend: 'down', change: 2, sparkline: '8,5 18,7 28,9 38,10 48,11 58,12 68,14 78,15 88,17' },
    { namc: 'TMMAL', score: 54, trend: 'down', change: 1, sparkline: '8,5 18,6 28,8 38,9 48,10 58,12 68,13 78,14 88,16' },
    { namc: 'TMMTX', score: 62, trend: 'down', change: 2, sparkline: '8,4 18,6 28,7 38,9 48,10 58,12 68,13 78,14 88,16' },
    { namc: 'TMMMT', score: 63, trend: 'up', change: 1, sparkline: '8,16 18,15 28,14 38,12 48,10 58,9 68,8 78,7 88,6' }
  ];

  readonly quickInsights = [
    {
      icon: 'pi-check-circle',
      iconClass: 'success',
      title: 'EDC utilization increased 1.6% vs last 30 days.',
      detail: 'Strong improvement across 4 of 7 NAMCs.'
    },
    {
      icon: 'pi-clone',
      iconClass: 'info',
      title: 'Report views increased 6.4% vs last 30 days.',
      detail: 'Top report: 2411 Equip Summary KPI (35,037 views)'
    },
    {
      icon: 'pi-exclamation-triangle',
      iconClass: 'warn',
      title: '2 NAMCs below 60% digital utilization score.',
      detail: 'Focus on EDC usage and report engagement.'
    },
    {
      icon: 'pi-clock',
      iconClass: 'primary',
      title: '11,913 hours saved in this period.',
      detail: 'Equivalent to 744 FTE hours.'
    }
  ];

  readonly gaugeScore = 75;

  trackByNamc(_: number, row: { namc: string }): string { return row.namc; }
  trackByIndex(index: number): number { return index; }

  selectTab(tab: 'overview' | 'edcUsage' | 'reportConsumption') {
    this.activeTab = tab;
  }

  constructor(private readonly dataService: ReportingDataService) {}
}
